'use client';

import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { MultiplayerSession, SessionParticipant, LocationUpdate } from '@/types/multiplayer';
import { useAuthContext } from './AuthContext';
import { RealtimeChannel } from '@supabase/supabase-js';

// Extend window type for debugging
declare global {
  interface Window {
    activeSession?: MultiplayerSession | null;
    participants?: SessionParticipant[];
    refreshSession?: () => Promise<void>;
    updateLocation?: (location: LocationUpdate) => Promise<void>;
    realtimeChannel?: RealtimeChannel | null;
  }
}

interface MultiplierContextType {
  // Session state
  activeSession: MultiplayerSession | null;
  participants: SessionParticipant[];
  isLoading: boolean;
  error: string | null;
  
  // Session management
  createSession: (options: CreateSessionOptions) => Promise<{ session?: MultiplayerSession; error?: string; message?: string }>;
  joinSession: (sessionCode: string) => Promise<{ session?: MultiplayerSession; error?: string; message?: string }>;
  leaveSession: () => Promise<{ error?: string }>;
  
  // Location updates
  updateLocation: (location: LocationUpdate) => Promise<void>;
  
  // State management
  clearError: () => void;
  refreshSession: () => Promise<void>;
}

interface CreateSessionOptions {
  routeId?: string;
  questId?: string;
  maxParticipants?: number;
  activityType?: 'route' | 'quest' | 'freestyle';
}

const MultiplierContext = createContext<MultiplierContextType | undefined>(undefined);

const STORAGE_KEY = 'multiplayer_session';
const MAX_RETRY_ATTEMPTS = 2; // Reduced from 3
const RETRY_DELAY = 500; // Reduced from 1000ms

// Simplified retry logic for edge function calls
async function retryEdgeFunction<T>(
  functionName: string,
  body: any,
  maxAttempts: number = MAX_RETRY_ATTEMPTS
): Promise<{ data?: T; error?: string }> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      console.log(`🔄 Attempting ${functionName} (${attempt}/${maxAttempts})`);
      
      const { data, error } = await supabase.functions.invoke(functionName, { body });
      
      if (error) {
        lastError = error;
        console.log(`❌ ${functionName} failed (attempt ${attempt}):`, error);
        
        // Don't retry on authentication errors
        if (error.message?.includes('auth') || error.message?.includes('unauthorized')) {
          break;
        }
        
        // Wait before retrying (shorter delay)
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
        continue;
      }
      
      console.log(`✅ ${functionName} succeeded on attempt ${attempt}`);
      return { data };
    } catch (err: any) {
      lastError = err;
      console.log(`❌ ${functionName} exception (attempt ${attempt}):`, err);
      
      // Wait before retrying
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * attempt));
      }
    }
  }
  
  const errorMsg = lastError?.message || `Failed to execute ${functionName} after ${maxAttempts} attempts`;
  return { error: errorMsg };
}

export function MultiplierProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuthContext();
  const [activeSession, setActiveSession] = useState<MultiplayerSession | null>(null);
  const [participants, setParticipants] = useState<SessionParticipant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realtimeChannel, setRealtimeChannel] = useState<RealtimeChannel | null>(null);

  // Persist session to localStorage
  const persistSession = useCallback((session: MultiplayerSession | null) => {
    if (typeof window !== 'undefined') {
      if (session) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          sessionId: session.id,
          sessionCode: session.session_code,
          timestamp: Date.now()
        }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Load session from localStorage
  const loadPersistedSession = useCallback(async () => {
    if (typeof window === 'undefined' || !isAuthenticated || !user?.id) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const { sessionId, timestamp } = JSON.parse(stored);
      
      // Check if session is recent (within 24 hours)
      const isRecent = Date.now() - timestamp < 24 * 60 * 60 * 1000;
      if (!isRecent) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Try to fetch the session
      const { data: session, error } = await supabase
        .from('route_sessions')
        .select(`
          *,
          walking_routes:route_id (
            id,
            name,
            description,
            difficulty,
            estimated_duration,
            total_distance
          ),
          session_participants (
            *,
            users (
              id,
              first_name,
              last_name,
              email
            )
          )
        `)
        .eq('id', sessionId)
        .eq('is_multiplayer', true)
        .single();

      if (error || !session) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Check if user is still a participant
      const isParticipant = session.session_participants?.some(
        (p: SessionParticipant) => p.user_id === user.id
      );

      if (!isParticipant) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Restore session
      setActiveSession(session);
      setParticipants(session.session_participants || []);
      console.log('🔄 Restored multiplayer session from storage:', session.session_code);
      
    } catch (err) {
      console.error('Error loading persisted session:', err);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isAuthenticated, user?.id]);

  // Refresh session data
  const refreshSession = useCallback(async () => {
    if (!activeSession) {
      console.log('⚠️ No active session to refresh');
      return;
    }

    console.log('🔄 Refreshing session data for:', activeSession.session_code);
    
    try {
      const { data: session, error } = await supabase
        .from('route_sessions')
        .select(`
          *,
          walking_routes:route_id (
            id,
            name,
            description,
            difficulty,
            estimated_duration,
            total_distance
          ),
          session_participants (
            *,
            users (
              id,
              first_name,
              last_name,
              email
            )
          )
        `)
        .eq('id', activeSession.id)
        .eq('is_multiplayer', true)
        .single();

      if (error || !session) {
        console.error('❌ Error refreshing session:', error);
        return;
      }

      console.log('✅ Session refreshed successfully:', {
        sessionCode: session.session_code,
        participantCount: session.session_participants?.length || 0,
        participants: session.session_participants?.map((p: any) => ({
          userId: p.user_id,
          name: p.users?.first_name || p.users?.email,
          status: p.status,
          hasLocation: !!(p.current_latitude && p.current_longitude)
        }))
      });

      setActiveSession(session);
      setParticipants(session.session_participants || []);
    } catch (err) {
      console.error('❌ Error refreshing session:', err);
    }
  }, [activeSession]);

  // Set up realtime subscription for active session
  const setupRealtimeSubscription = useCallback((sessionId: string) => {
    console.log('🔗 Setting up realtime subscription for session:', sessionId);
    
    if (realtimeChannel) {
      console.log('🔌 Unsubscribing from previous channel');
      realtimeChannel.unsubscribe();
    }

    const channel = supabase
      .channel(`session:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_participants',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          console.log('🔄 Multiplayer realtime update:', {
            eventType: payload.eventType,
            sessionId: sessionId,
            participantId: (payload.new as any)?.id || (payload.old as any)?.id,
            userId: (payload.new as any)?.user_id || (payload.old as any)?.user_id
          });
          
          if (payload.eventType === 'INSERT') {
            // New participant joined - refresh session to get full user data
            console.log('➕ New participant joined, refreshing session', {
              participantId: (payload.new as any)?.id,
              userId: (payload.new as any)?.user_id,
              currentParticipantCount: participants.length
            });
            // Use a slight delay to ensure the database insert is fully committed
            setTimeout(() => {
              refreshSession();
            }, 100);
          } else if (payload.eventType === 'UPDATE') {
            // Participant location/status updated
            const updatedParticipant = payload.new as SessionParticipant;
            console.log('🔄 Updating participant:', updatedParticipant.user_id, {
              latitude: updatedParticipant.current_latitude,
              longitude: updatedParticipant.current_longitude,
              status: updatedParticipant.status,
              lastLocationUpdate: updatedParticipant.last_location_update
            });
            
            setParticipants(prev => {
              const updated = prev.map(p => 
                p.id === updatedParticipant.id 
                  ? { ...p, ...updatedParticipant, users: p.users } // Preserve existing users data
                  : p
              );
              console.log('👥 Updated participants list:', {
                oldCount: prev.length,
                newCount: updated.length,
                participants: updated.map(p => ({
                  userId: p.user_id,
                  name: p.users?.first_name || p.users?.email?.split('@')[0],
                  hasLocation: !!(p.current_latitude && p.current_longitude),
                  status: p.status
                }))
              });

              return updated;
            });
          } else if (payload.eventType === 'DELETE') {
            // Participant left
            console.log('➖ Participant left:', (payload.old as any)?.user_id, {
              participantId: (payload.old as any)?.id,
              currentParticipantCount: participants.length
            });
            setParticipants(prev => {
              const filtered = prev.filter(p => p.id !== (payload.old as any).id);
              console.log('👥 Updated participants list after deletion:', {
                oldCount: prev.length,
                newCount: filtered.length
              });

              return filtered;
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('🔗 Multiplayer realtime connection status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to realtime updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime channel error');
        } else if (status === 'TIMED_OUT') {
          console.error('⏰ Realtime connection timed out');
        } else if (status === 'CLOSED') {
          console.warn('🔴 Realtime connection closed');
        }
      });

    setRealtimeChannel(channel);
    console.log('✅ Realtime channel setup complete');
  }, [realtimeChannel, refreshSession]);

  // Create multiplayer session with retry logic
  const createSession = useCallback(async (options: CreateSessionOptions) => {
    if (!user) return { error: 'User not authenticated' };

    setIsLoading(true);
    setError(null);

    try {
      const result = await retryEdgeFunction('create-multiplayer-session', {
        routeId: options.routeId,
        questId: options.questId,
        maxParticipants: options.maxParticipants || 10,
        activityType: options.activityType || 'freestyle'
      });

      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }

      const session = (result.data as any)?.session;
      if (!session) {
        const errorMsg = 'Invalid session data received';
        setError(errorMsg);
        return { error: errorMsg };
      }

      setActiveSession(session);
      setParticipants(session.session_participants || []);
      persistSession(session);
      
      // Set up realtime subscription after a brief delay to ensure session is fully established
      setTimeout(() => {
        setupRealtimeSubscription(session.id);
      }, 500);

      const message = (result.data as any)?.message;
      
      console.log('✅ Created multiplayer session:', {
        sessionCode: session.session_code,
        participantCount: session.session_participants?.length || 0,
        message: message
      });
      
      return { session, message };


    } catch (err: any) {
      const errorMsg = err.message || 'Failed to create session';
      setError(errorMsg);
      return { error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [user, persistSession, setupRealtimeSubscription]);

  // Join multiplayer session with retry logic
  const joinSession = useCallback(async (sessionCode: string) => {
    if (!user) return { error: 'User not authenticated' };

    setIsLoading(true);
    setError(null);

    try {
      const result = await retryEdgeFunction('join-session', {
        sessionCode
      });

      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }

      const session = (result.data as any)?.session;
      if (!session) {
        const errorMsg = 'Invalid session data received';
        setError(errorMsg);
        return { error: errorMsg };
      }

      setActiveSession(session);
      setParticipants(session.session_participants || []);
      persistSession(session);
      
      // Set up realtime subscription after a brief delay to ensure session is fully established
      setTimeout(() => {
        setupRealtimeSubscription(session.id);
      }, 500);

      const message = (result.data as any)?.message;
      
      console.log('✅ Joined multiplayer session:', {
        sessionCode: session.session_code,
        participantCount: session.session_participants?.length || 0,
        message: message
      });
      
      return { session, message };

    } catch (err: any) {
      const errorMsg = err.message || 'Failed to join session';
      setError(errorMsg);
      return { error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [user, persistSession, setupRealtimeSubscription]);

  // Leave multiplayer session
  const leaveSession = useCallback(async () => {
    if (!activeSession || !user) return { error: 'No active session' };

    setIsLoading(true);
    setError(null);

    try {
      // Remove participant from database
      const { error } = await supabase
        .from('session_participants')
        .delete()
        .eq('session_id', activeSession.id)
        .eq('user_id', user.id);

      if (error) {
        const errorMsg = error.message || 'Failed to leave session';
        setError(errorMsg);
        return { error: errorMsg };
      }

      // Clean up local state
      if (realtimeChannel) {
        realtimeChannel.unsubscribe();
        setRealtimeChannel(null);
      }

      setActiveSession(null);
      setParticipants([]);
      persistSession(null);

      console.log('✅ Left multiplayer session');
      return {};

    } catch (err: any) {
      const errorMsg = err.message || 'Failed to leave session';
      setError(errorMsg);
      return { error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [activeSession, user, realtimeChannel, persistSession]);

  // Update location in session with retry logic
  const updateLocation = useCallback(async (location: LocationUpdate) => {
    if (!activeSession) {
      console.log('⚠️ No active session for location update');
      return;
    }

    console.log('📍 Updating location in session:', {
      sessionId: activeSession.id,
      sessionCode: activeSession.session_code,
      latitude: location.latitude,
      longitude: location.longitude
    });

    try {
      const result = await retryEdgeFunction('update-location', {
        sessionId: activeSession.id,
        latitude: location.latitude,
        longitude: location.longitude,
        waypointIndex: location.waypointIndex || 0
      }, 1); // Single retry for location updates

      if (result.error) {
        console.error('❌ Error updating location after retries:', result.error);
      } else {
        console.log('✅ Location updated successfully');
      }
    } catch (err) {
      console.error('❌ Error updating location:', err);
    }
  }, [activeSession]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load persisted session on auth change
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      console.log('🔑 User authenticated, loading persisted session');
      loadPersistedSession();
    } else {
      console.log('🚪 User not authenticated, clearing session');
      // Clear session when user logs out
      setActiveSession(null);
      setParticipants([]);
      if (realtimeChannel) {
        console.log('🔌 Unsubscribing from realtime channel on logout');
        realtimeChannel.unsubscribe();
        setRealtimeChannel(null);
      }
      persistSession(null);
    }
  }, [isAuthenticated, user?.id, loadPersistedSession, realtimeChannel, persistSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (realtimeChannel) {
        realtimeChannel.unsubscribe();
      }
    };
  }, [realtimeChannel]);

  const value: MultiplierContextType = {
    activeSession,
    participants,
    isLoading,
    error,
    createSession,
    joinSession,
    leaveSession,
    updateLocation,
    clearError,
    refreshSession
  };

  // Expose debugging values to window for development
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.activeSession = activeSession;
      window.participants = participants;
      window.refreshSession = refreshSession;
      window.updateLocation = updateLocation;
      window.realtimeChannel = realtimeChannel;
    }
  }, [activeSession, participants, refreshSession, updateLocation, realtimeChannel]);

  return (
    <MultiplierContext.Provider value={value}>
      {children}
    </MultiplierContext.Provider>
  );
}

export function useMultiplierContext() {
  const context = useContext(MultiplierContext);
  if (context === undefined) {
    throw new Error('useMultiplierContext must be used within a MultiplierProvider');
  }
  return context;
}